"use client";

/**
 * <TabsProvider /> — 크롬 탭 시스템 (사용자 결정 2026-05-09).
 *
 * 모델:
 *   - 탭 = { id, type: 'home'|'dashboard'|'analyze', ticker?, title }
 *   - 탭 type 별 URL 매핑: home → '/', dashboard → '/#dashboard', analyze → '/analyze/[ticker]'
 *   - 활성 탭 변경 = router.push(URL) (URL 정책 a — 사용자 선택)
 *   - 동일 type+payload 의 탭이 이미 있으면 그 탭 활성 (중복 X)
 *
 * 영속성:
 *   Phase A1 (이번): localStorage 'tabs:v1'
 *   Phase A2 (다음 PR): server '/api/user/tabs' 우선, localStorage fallback
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";

export type TabType = "home" | "dashboard" | "analyze";

export interface Tab {
  id: string;
  type: TabType;
  ticker?: string; // analyze 일 때 필수
  title: string;
  createdAt: number;
}

interface TabsState {
  tabs: Tab[];
  activeTabId: string | null;
}

interface TabsContextValue extends TabsState {
  /** 같은 type+payload 가 있으면 그 탭 활성, 없으면 신규 탭 + 활성. URL navigation 동반.
   *  forceNew=true 면 항상 새 탭 (+ 버튼용). */
  openTab: (type: TabType, payload?: { ticker?: string; title?: string; forceNew?: boolean }) => string;
  /** 활성 탭의 type/ticker/title 변경 (예: home → analyze 전환). URL navigation 동반. */
  updateTab: (id: string, patch: Partial<Tab>) => void;
  /** 탭 닫기. 활성 탭 닫으면 인접 탭 활성. 마지막 탭 닫으면 새 home 탭. */
  closeTab: (id: string) => void;
  /** 명시 활성. URL navigation 동반. */
  activateTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);
// v2 (2026-05-09) — defaultTitle 변경 + dashboard hash 추적 → 옛 탭 무시
const STORAGE_KEY = "tabs:v2";

function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function urlOf(tab: Tab): string {
  if (tab.type === "home") return "/";
  if (tab.type === "dashboard") return "/#dashboard";
  if (tab.type === "analyze" && tab.ticker) return `/analyze/${tab.ticker.toLowerCase()}`;
  return "/";
}

function defaultTitle(type: TabType, ticker?: string): string {
  if (type === "home") return "홈";
  if (type === "dashboard") return "대시보드";
  // analyze: 'NVDA | 기업분석' (사용자 결정 2026-05-09)
  if (type === "analyze") return `${ticker?.toUpperCase() || "분석"} | 기업분석`;
  return "탭";
}

function loadFromStorage(): TabsState {
  if (typeof window === "undefined") return { tabs: [], activeTabId: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tabs: [], activeTabId: null };
    const parsed = JSON.parse(raw) as TabsState;
    if (!Array.isArray(parsed.tabs)) return { tabs: [], activeTabId: null };
    return parsed;
  } catch {
    return { tabs: [], activeTabId: null };
  }
}

function saveToStorage(state: TabsState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — 무시 */
  }
}

export function TabsProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() || "/";

  // SSR 안전 — 초기 빈 상태, mount 후 localStorage 로 hydrate
  const [state, setState] = useState<TabsState>({ tabs: [], activeTabId: null });
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const loaded = loadFromStorage();
    setState(loaded);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated) saveToStorage(state);
  }, [state, hydrated]);

  // hash 추적 (#dashboard 인식) — usePathname 은 hash 안 줌, hashchange listener 필요
  const [hashState, setHashState] = useState<string>("");
  useEffect(() => {
    if (typeof window === "undefined") return;
    setHashState(window.location.hash || "");
    const onHashChange = () => setHashState(window.location.hash || "");
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // 현재 URL (pathname + hash) 에 매칭되는 탭이 있는지 — 없으면 자동 추가
  useEffect(() => {
    if (!hydrated) return;
    const tickerMatch = pathname.match(/^\/analyze\/([^/]+)/i);
    let inferType: TabType = "home";
    let inferTicker: string | undefined = undefined;
    if (tickerMatch) {
      inferType = "analyze";
      inferTicker = tickerMatch[1].toUpperCase();
    } else if (pathname === "/" && hashState === "#dashboard") {
      inferType = "dashboard";
    } else {
      inferType = "home";
    }

    setState((prev) => {
      const existing = prev.tabs.find((t) => {
        if (t.type !== inferType) return false;
        if (inferType === "analyze") return (t.ticker || "").toUpperCase() === (inferTicker || "");
        return true;
      });
      if (existing) {
        if (prev.activeTabId === existing.id) return prev;
        return { ...prev, activeTabId: existing.id };
      }
      const newTab: Tab = {
        id: uid(),
        type: inferType,
        ticker: inferTicker,
        title: defaultTitle(inferType, inferTicker),
        createdAt: Date.now(),
      };
      return { tabs: [...prev.tabs, newTab], activeTabId: newTab.id };
    });
  }, [pathname, hashState, hydrated]);

  const openTab = useCallback((type: TabType, payload?: { ticker?: string; title?: string; forceNew?: boolean }) => {
    let createdId: string | null = null;
    setState((prev) => {
      // forceNew 면 매칭 무시 (+ 버튼 같이 항상 새 탭)
      if (!payload?.forceNew) {
        const existing = prev.tabs.find((t) => {
          if (t.type !== type) return false;
          if (type === "analyze") return (t.ticker || "").toUpperCase() === (payload?.ticker?.toUpperCase() || "");
          return true;
        });
        if (existing) {
          createdId = existing.id;
          return { ...prev, activeTabId: existing.id };
        }
      }
      const tab: Tab = {
        id: uid(),
        type,
        ticker: payload?.ticker,
        title: payload?.title || defaultTitle(type, payload?.ticker),
        createdAt: Date.now(),
      };
      createdId = tab.id;
      return { tabs: [...prev.tabs, tab], activeTabId: tab.id };
    });
    // URL 동기화
    setTimeout(() => {
      const target = state.tabs.find((t) => t.id === createdId)
        || { id: createdId!, type, ticker: payload?.ticker, title: "", createdAt: 0 } as Tab;
      router.push(urlOf(target));
    }, 0);
    return createdId!;
  }, [router, state.tabs]);

  const updateTab = useCallback((id: string, patch: Partial<Tab>) => {
    setState((prev) => {
      const tabs = prev.tabs.map((t) => (t.id === id ? { ...t, ...patch } : t));
      return { ...prev, tabs };
    });
    // URL 동기화 (활성 탭일 때만)
    setTimeout(() => {
      setState((prev) => {
        if (prev.activeTabId === id) {
          const target = prev.tabs.find((t) => t.id === id);
          if (target) router.push(urlOf(target));
        }
        return prev;
      });
    }, 0);
  }, [router]);

  const closeTab = useCallback((id: string) => {
    setState((prev) => {
      const idx = prev.tabs.findIndex((t) => t.id === id);
      if (idx < 0) return prev;
      const remaining = prev.tabs.filter((t) => t.id !== id);
      let nextActive = prev.activeTabId;
      if (prev.activeTabId === id) {
        // 인접 탭 활성 (오른쪽 우선, 없으면 왼쪽)
        const neighbor = remaining[idx] || remaining[idx - 1] || null;
        nextActive = neighbor?.id || null;
      }
      // 마지막 탭 닫음 → 새 home
      if (remaining.length === 0) {
        const home: Tab = { id: uid(), type: "home", title: defaultTitle("home"), createdAt: Date.now() };
        setTimeout(() => router.push("/"), 0);
        return { tabs: [home], activeTabId: home.id };
      }
      // 활성 변경 시 URL 동기화
      if (nextActive && nextActive !== prev.activeTabId) {
        const target = remaining.find((t) => t.id === nextActive);
        if (target) setTimeout(() => router.push(urlOf(target)), 0);
      }
      return { tabs: remaining, activeTabId: nextActive };
    });
  }, [router]);

  const activateTab = useCallback((id: string) => {
    setState((prev) => {
      const target = prev.tabs.find((t) => t.id === id);
      if (!target) return prev;
      if (prev.activeTabId !== id) {
        setTimeout(() => router.push(urlOf(target)), 0);
      }
      return { ...prev, activeTabId: id };
    });
  }, [router]);

  return (
    <TabsContext.Provider value={{ ...state, openTab, updateTab, closeTab, activateTab }}>
      {children}
    </TabsContext.Provider>
  );
}

export function useTabs(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    // provider 밖 — noop fallback
    return {
      tabs: [],
      activeTabId: null,
      openTab: () => "",
      updateTab: () => {},
      closeTab: () => {},
      activateTab: () => {},
    };
  }
  return ctx;
}
